import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export interface RecognisedEmailLandingProps {
  /**
   * Auth-zero `DELETE /sessions/:id` URL. Powers the primary "Log out" CTA
   * on the wrong-account dead-end. When null the card does NOT render
   * (fail-safe — no useless empty branch).
   */
  sign_out_url: string | null
  className?: string
}

/**
 * RecognisedEmailLanding — OKS-675.
 *
 * Historically (OKS-24/319) this rendered the whole recognised-email
 * experience, including the invitee **email** and a bounce-to-`/sign_in`
 * Variant B. Both were privacy/UX bugs (OKS-675): the email leaked on a
 * shareable link and the sign-in bounce never completed the crew join.
 *
 * The welcome sub-variants (logged-in match → one-tap join; logged-out →
 * "Continue with TikTok") now render the full brand welcome
 * (`<WelcomeLanding>` + `<RecognisedJoinBar>` / `<FloatingCta>`). This
 * component is reduced to the ONLY remaining recognised dead-end: the
 * **wrong-account** card (logged in as a DIFFERENT user than the invited
 * creator). It shows NO email — just a headline, body, and a primary "Log
 * out" CTA so the user can re-open the invite from the right account.
 *
 * Wire format: `<form method=post>` + hidden `_method=delete` + CSRF —
 * auth-zero session destroy is `DELETE /sessions/:id` and browsers don't
 * speak DELETE on submit (Rack::MethodOverride reads the body). testids
 * `invite-landing-recognised-wrong-account` + `-logout` / `-logout-submit`
 * are preserved so existing Playwright assertions hold.
 */
export function RecognisedEmailLanding({
  sign_out_url,
  className,
}: RecognisedEmailLandingProps) {
  const { t } = useTranslation()

  if (!sign_out_url) return null

  return (
    <div
      data-testid="invite-landing-recognised-wrong-account"
      className={cn("flex flex-col items-stretch gap-3", className)}
    >
      <h2 className="text-foreground text-base font-semibold">
        {t("invites.landing.error.recognised.wrong_account.headline")}
      </h2>
      <p className="text-muted-foreground text-sm">
        {t("invites.landing.error.recognised.wrong_account.body")}
      </p>
      <form
        method="post"
        action={sign_out_url}
        data-testid="invite-landing-recognised-logout"
        className="contents"
      >
        <input type="hidden" name="_method" value="delete" />
        <CsrfHiddenInput />
        <Button
          type="submit"
          size="lg"
          variant="default"
          className="w-full"
          data-testid="invite-landing-recognised-logout-submit"
        >
          {t("invites.landing.error.recognised.wrong_account.cta")}
        </Button>
      </form>
    </div>
  )
}

function CsrfHiddenInput() {
  // Reads <meta name="csrf-token"> at render time. Returns nothing on
  // surfaces where the meta isn't in the DOM (isolated tests) — the form
  // then fails server-side CSRF, which is the correct behaviour.
  if (typeof document === "undefined") return null
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )
  if (!meta?.content) return null
  return <input type="hidden" name="authenticity_token" value={meta.content} />
}

export default RecognisedEmailLanding
